import { useMemo } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Select } from "antd";
import ActionsByCategory from "../../Roles/Permissions/ActionsByCategory/ActionsByCategory";
import {
  READ_ONLY_ACCESS_KEY,
  readOnlyAccessRow,
} from "../../Roles/Permissions/readOnlyAccessRow";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useGetPermissionsSchema, useGetRoles } from "../../../../queries/useRolesApi";
import { useGetStaffRolesDropdown } from "../../../../queries/useGetStaffDropdowns";
import "./stylesRolePermissions.css";
import "../../Roles/Actions/stylesNewRole.css";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import SidebarPreview from "../../../StaffManagement/Roles/SidebarPreview/SidebarPreview";

/** Convert API role.actions/role.permissions (string[]) to { [key]: true } for toggles */
function arrayToToggleObject(arr) {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, key) => ({ ...acc, [key]: true }), {});
}

/** Fallback static roles when API not yet loaded (e.g. tests) */
const FALLBACK_ROLES = [
  { id: "superadmin", name: "Superadmin", color: "#3B82F6" },
  { id: "manager", name: "Manager", color: "#EC4899" },
];

/** Fallback static actions when schema not loaded */
const FALLBACK_ACTIONS = [
  { key: "edit_transaction_routing", display_name: "Edit Transaction Routing" },
  { key: "edit_sportsbook_fm", display_name: "Edit Sportsbook Feature Management" },
  { key: "edit_casino_fm", display_name: "Edit Casino Feature Management" },
  { key: "add_promo", display_name: "Add a Promo" },
  { key: "settle_bet", display_name: "Settle a Bet" },
  { key: "review_withdrawal", display_name: "Review Withdrawal" },
  { key: "edit_user_wallet", display_name: "Edit User Wallet" },
  { key: "add_limits", display_name: "Add Limits" },
  { key: "create_edit_pam_role", display_name: "Create a PAM User" },
  { key: "suspend_user", display_name: "Suspend/Delete a PAM User" },
  { key: "suspend_user_alt", display_name: "Suspend a User" },
  { key: "unsuspend_user", display_name: "Unsuspend a User" },
  { key: "delete_user", display_name: "Delete a User" },
  { key: "mcp_access", display_name: "MCP Access" },
];

function RolePermissions({
  roleInfo,
  onRoleChange,
  onRoleSelected,
  onActionToggle,
  isReadOnly = false,
}) {
  const { data: schema, isLoading: schemaLoading } = useGetPermissionsSchema();
  const { data: apiRoles, isLoading: rolesLoading } = useGetRoles();
  const { data: staffRolesDropdown, isLoading: dropdownLoading } = useGetStaffRolesDropdown();

  // Use staff roles dropdown (role_id, role_name, role_color) for display
  const roles = useMemo(() => {
    // Prefer staff dropdown if available (returns { value: role_id, label: role_name, color })
    if (Array.isArray(staffRolesDropdown) && staffRolesDropdown.length > 0) {
      return staffRolesDropdown.map((r) => ({
        id: r.value,        // role_id
        name: r.label,      // role_name
        color: r.color ?? "#6b7280",  // role_color
      }));
    }
    // Fallback to apiRoles or static fallback
    const list = Array.isArray(apiRoles) && apiRoles.length > 0 ? apiRoles : FALLBACK_ROLES;
    return list.map((r) => ({
      id: r.id,
      name: r.name ?? r.id,
      color: r.color ?? "#6b7280",
    }));
  }, [staffRolesDropdown, apiRoles]);

  const actionsList = useMemo(() => {
    const list = schema?.available_actions;
    const available =
      Array.isArray(list) && list.length > 0
        ? list.map((a) => ({ key: a.key, display_name: a.display_name ?? a.key }))
        : FALLBACK_ACTIONS;
    // The lock is set on the Roles page — reported here, never edited, so it
    // can't leak into the role's `actions` payload.
    return [{ ...readOnlyAccessRow(), disabled: true }, ...available];
  }, [schema?.available_actions]);

  // The lock is a property of the selected role; this step only displays it.
  const actionValues = useMemo(() => {
    const selected = apiRoles?.find(
      (r) => r.id === roleInfo.role || r.id === String(roleInfo.role),
    );
    return {
      ...roleInfo.actions,
      [READ_ONLY_ACCESS_KEY]: Boolean(selected?.read_only_access),
    };
  }, [apiRoles, roleInfo.role, roleInfo.actions]);

  const handleRoleChange = (value) => {
    onRoleChange?.(value);
    const selected = roles.find((r) => r.id === value || r.id === String(value));
    if (selected && apiRoles?.length) {
      const fullRole = apiRoles.find((r) => r.id === value || r.id === String(value));
      if (fullRole && onRoleSelected) {
        onRoleSelected({
          actions: arrayToToggleObject(fullRole.actions),
          permissions: arrayToToggleObject(fullRole.permissions),
        });
      }
    }
  };

  const isLoading = schemaLoading || rolesLoading || dropdownLoading;

  if (isLoading) {
    return (
      <div className="role-permissions-container">
        <div className="step-content-header">
          <h3>{AppStrings.ROLE}</h3>
        </div>
        <div className="loading-container">
        <RebetLoader height="100px" width="100px" />
        </div>
      </div>
    );
  }

  return (
    <div className="role-permissions-container">
      <div className="step-content-header">
        <h3>{AppStrings.ROLE}</h3>
        <InfoCircleOutlined className="info-icon" />
      </div>
      <div className="role-selection">
        <Select
          placeholder={AppStrings.CHOOSE_THE_ROLE}
          value={roleInfo.role ?? undefined}
          onChange={handleRoleChange}
          className="role-select"
          virtual={false}
          options={roles.map((role) => ({
            value: role.id,
            label: (
              <div className="role-option">
                <div
                  className="role-color-dot"
                  style={{ backgroundColor: role.color }}
                />
                <span>{role.name}</span>
              </div>
            ),
          }))}
        />
      </div>

      <div className="staff-management-new-role-body-actions-container">
        <div className="staff-management-new-role-body-actions-header">
        <span className="staff-management-new-role-body-actions-label">Sidebar Preview</span>
        <span className="staff-management-new-role-body-actions-label">
          {AppStrings.ACTIONS}
        </span>
        </div>
        <div className="staff-management-new-role-body-overview-container">
          <div className="staff-management-new-role-body-sidebar-preview">
            <SidebarPreview
              permissions={roleInfo.permissions}
              availablePermissions={schema?.available_permissions}
            />
          </div>
          <div className="staff-management-new-role-body-actions">
            <ActionsByCategory
              actions={actionsList}
              values={actionValues}
              onActionToggle={onActionToggle}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default RolePermissions;
